import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { UserPlus, Search, Edit, Trash2, Phone, Mail, KeyRound, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { formatPhoneBR, normalizePhone } from '../../lib/phone';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const emptyForm = { name: '', rg: '', cpf: '', email: '', phone: '', birth_date: '', address: '', consultation_type: 'clinica_geral' };

export const PatientsManagement = () => {
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [form, setForm] = useState(emptyForm);

  // Reset password dialog state
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const fetchPatients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/patients`, { withCredentials: true });
      setPatients(data);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  }, []);

  useEffect(() => { fetchPatients(); }, [fetchPatients]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      if (payload.phone) payload.phone = normalizePhone(payload.phone);
      await axios.post(`${API_URL}/api/patients`, payload, { withCredentials: true });
      toast.success('Paciente cadastrado com sucesso!');
      setIsCreateOpen(false);
      setForm(emptyForm);
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar paciente');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const updateData = {};
      Object.keys(form).forEach(key => { if (form[key]) updateData[key] = form[key]; });
      if (updateData.phone) updateData.phone = normalizePhone(updateData.phone);
      await axios.put(`${API_URL}/api/patients/${editingPatient.patient_id}`, updateData, { withCredentials: true });
      toast.success('Paciente atualizado!');
      setEditingPatient(null);
      setForm(emptyForm);
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar');
    }
  };

  const handleStatusChange = async (patientId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/patients/${patientId}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Status alterado para ${newStatus}`);
      fetchPatients();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const handleDelete = async (patientId) => {
    if (!window.confirm('Tem certeza que deseja excluir este paciente?')) return;
    try {
      await axios.delete(`${API_URL}/api/patients/${patientId}`, { withCredentials: true });
      toast.success('Paciente excluído');
      fetchPatients();
    } catch (error) {
      toast.error('Erro ao excluir paciente');
    }
  };

  const startEdit = (patient) => {
    setEditingPatient(patient);
    setForm({
      name: patient.name || '',
      rg: patient.rg || '',
      cpf: patient.cpf || '',
      email: patient.email || '',
      phone: formatPhoneBR(patient.phone || ''),
      birth_date: patient.birth_date || '',
      address: patient.address || '',
      consultation_type: patient.consultation_type || 'clinica_geral'
    });
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 4) {
      toast.error('A senha deve ter no mínimo 4 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('As senhas não conferem');
      return;
    }
    setResetLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/patients/${resetTarget.patient_id}/reset-password`,
        { new_password: newPassword },
        { withCredentials: true }
      );
      toast.success(`Senha de ${resetTarget.name} redefinida com sucesso`);
      setResetTarget(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao redefinir senha');
    }
    setResetLoading(false);
  };

  const filtered = patients.filter(p => {
    const s = search.toLowerCase();
    const phoneDigits = normalizePhone(search);
    const matchesSearch = p.name?.toLowerCase().includes(s) ||
      p.cpf?.includes(search) || p.email?.toLowerCase().includes(s) ||
      (phoneDigits && (p.phone || '').includes(phoneDigits));
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    const matchesType = typeFilter === 'all' || (p.consultation_type || 'clinica_geral') === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const statusColors = {
    active: 'bg-[#0F1B3D]/10 text-[#0F1B3D]',
    inactive: 'bg-[#7A85A0]/10 text-[#7A85A0]',
    cancelled: 'bg-[#A8174E]/10 text-[#A8174E]'
  };

  const statusLabels = { active: 'Ativo', inactive: 'Inativo', cancelled: 'Cancelado' };

  const renderForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Nome Completo *</Label>
          <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="patient-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Celular *</Label>
          <Input value={form.phone} onChange={e => setForm({...form, phone: formatPhoneBR(e.target.value)})} required inputMode="numeric" maxLength={15} placeholder="(00) 00000-0000" data-testid="patient-phone-input" />
        </div>
        <div className="space-y-2">
          <Label>CPF</Label>
          <Input value={form.cpf} onChange={e => setForm({...form, cpf: e.target.value})} placeholder="000.000.000-00" data-testid="patient-cpf-input" />
        </div>
        <div className="space-y-2">
          <Label>RG</Label>
          <Input value={form.rg} onChange={e => setForm({...form, rg: e.target.value})} data-testid="patient-rg-input" />
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="patient-email-input" />
        </div>
        <div className="space-y-2">
          <Label>Data de Nascimento</Label>
          <Input type="date" value={form.birth_date} onChange={e => setForm({...form, birth_date: e.target.value})} data-testid="patient-birthdate-input" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Endereço</Label>
        <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} data-testid="patient-address-input" />
      </div>
      <div className="space-y-2">
        <Label>Tipo de Consulta *</Label>
        <Select value={form.consultation_type} onValueChange={val => setForm({...form, consultation_type: val})}>
          <SelectTrigger data-testid="patient-consultation-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="clinica_geral">Clínica Geral</SelectItem>
            <SelectItem value="tirzepatida">Tirzepatida</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" className="w-full bg-[#A8174E] hover:bg-[#8E1142] text-white" data-testid="patient-submit-button">
        {submitLabel}
      </Button>
    </form>
  );

  return (
    <div className="space-y-6" data-testid="patients-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-medium text-[#0F1B3D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Pacientes</h2>
          <p className="text-[#5F6B85]">Apenas Nome e Celular são obrigatórios</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={(open) => { setIsCreateOpen(open); if (!open) setForm(emptyForm); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#A8174E] hover:bg-[#8E1142] text-white" data-testid="add-patient-button">
              <UserPlus className="h-4 w-4 mr-2" />
              Novo Paciente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Cadastrar Paciente</DialogTitle>
            </DialogHeader>
            {renderForm(handleCreate, 'Cadastrar')}
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-[#7A85A0]" />
          <Input
            placeholder="Buscar por nome, telefone, CPF ou email..."
            className="pl-10"
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="search-patients-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos Status</SelectItem>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
            <SelectItem value="cancelled">Cancelados</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="type-filter">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos Tipos</SelectItem>
            <SelectItem value="clinica_geral">Clínica Geral</SelectItem>
            <SelectItem value="tirzepatida">Tirzepatida</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingPatient} onOpenChange={(open) => { if (!open) { setEditingPatient(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Paciente</DialogTitle>
          </DialogHeader>
          {renderForm(handleUpdate, 'Atualizar')}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(open) => { if (!open) { setResetTarget(null); setNewPassword(''); setConfirmPassword(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Redefinir Senha</DialogTitle>
          </DialogHeader>
          {resetTarget && (
            <form onSubmit={handleResetPassword} className="space-y-4" data-testid="reset-password-form">
              <p className="text-sm text-[#5F6B85]">
                Defina uma nova senha para <strong>{resetTarget.name}</strong>. Compartilhe com o paciente após a alteração.
              </p>
              <div className="space-y-2">
                <Label>Nova senha *</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={4}
                  required
                  data-testid="reset-new-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Confirmar nova senha *</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={4}
                  required
                  data-testid="reset-confirm-password-input"
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => { setResetTarget(null); setNewPassword(''); setConfirmPassword(''); }} data-testid="reset-cancel-button">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-[#A8174E] hover:bg-[#8E1142] text-white" disabled={resetLoading} data-testid="reset-submit-button">
                  {resetLoading ? 'Salvando...' : 'Redefinir'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 gap-4">
        {filtered.length === 0 ? (
          <Card className="border-[#E5DCC8]">
            <CardContent className="p-8 text-center">
              <p className="text-[#7A85A0]">Nenhum paciente encontrado</p>
            </CardContent>
          </Card>
        ) : (
          filtered.map((patient) => (
            <Card key={patient.patient_id} className="border-[#E5DCC8] hover:-translate-y-0.5 transition-all duration-200" data-testid={`patient-card-${patient.patient_id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-[#0F1B3D]">{patient.name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[patient.status] || statusColors.active}`}>
                        {statusLabels[patient.status] || 'Ativo'}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${(patient.consultation_type) === 'tirzepatida' ? 'bg-[#B8845C]/10 text-[#B8845C]' : 'bg-[#0F1B3D]/10 text-[#0F1B3D]'}`}>
                        {patient.consultation_type === 'tirzepatida' ? 'Tirzepatida' : 'Clínica Geral'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm text-[#5F6B85]">
                      <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{formatPhoneBR(patient.phone) || '-'}</span>
                      {patient.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{patient.email}</span>}
                      {patient.cpf && <span>CPF: {patient.cpf}</span>}
                      {patient.rg && <span>RG: {patient.rg}</span>}
                      {patient.created_at && (
                        <span className="flex items-center gap-1" title="Data de cadastro">
                          <Calendar className="h-3 w-3" />
                          Cadastro: {new Date(patient.created_at).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Select onValueChange={(val) => handleStatusChange(patient.patient_id, val)}>
                      <SelectTrigger className="w-[130px]" data-testid={`status-select-${patient.patient_id}`}>
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Ativo</SelectItem>
                        <SelectItem value="inactive">Inativo</SelectItem>
                        <SelectItem value="cancelled">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={() => setResetTarget(patient)} title="Redefinir senha" data-testid={`reset-password-${patient.patient_id}`}>
                      <KeyRound className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => startEdit(patient)} data-testid={`edit-patient-${patient.patient_id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(patient.patient_id)} className="text-[#A8174E] hover:text-[#A8174E]" data-testid={`delete-patient-${patient.patient_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
